/**
 * @file SkillsTable.jsx
 * @description SkillsTable component for displaying and managing skills table.
 * @component SkillsTable
 * @exports SkillsTable - Main component for displaying and managing skills table.
 */

import React from "react";
import Table from "@mui/material/Table";
import Switch from '@mui/material/Switch';
import { Edit } from "@mui/icons-material";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { SKILL_TABLE_COLUMN_KEYS, SKILL_MESSAGES, SKILL_STRINGS } from "../../../common/constants/adminModule/skillConstants.js";

const SkillsTable = ({
    page,
    order,
    setPage,
    orderBy,
    skills,
    rowsPerPage,
    columns,
    isSkillsLoading,
    setRowsPerPage,
    onEdit,
    handleRequestSort,
    filteredSkillsLength
}) => {
    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    align={column.id === SKILL_TABLE_COLUMN_KEYS.ACTIVE ? ALIGN.RIGHT : ALIGN.LEFT}
                                    sx={{
                                        width:
                                            column.id === SKILL_TABLE_COLUMN_KEYS.ACTIONS ? 50 :
                                            column.id === SKILL_TABLE_COLUMN_KEYS.ID ? 100 :
                                            column.id === SKILL_TABLE_COLUMN_KEYS.SKILL_NAME ? 200 :
                                            column.id === SKILL_TABLE_COLUMN_KEYS.QUEUE_ID ? 200 :
                                            column.id === SKILL_TABLE_COLUMN_KEYS.CHANNEL_TYPE ? 150 :
                                            column.id === SKILL_TABLE_COLUMN_KEYS.CREATE_DATE ? 100 : 
                                            column.id === SKILL_TABLE_COLUMN_KEYS.ACTIVE ? 150 : 200
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isSkillsLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER} >
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : skills.length > 0 ? skills.map((skill) => (
                            <TableRow hover tabIndex={-1} key={skill.id}>
                                <TableCell>
                                    <IconButton onClick={() => onEdit(skill)}>
                                        <Edit fontSize={SIZES.SMALL} />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    {skill.id}
                                </TableCell>
                                <TableCell>
                                    {skill.skillname}
                                </TableCell>
                                <TableCell>
                                    {skill.queueid}
                                </TableCell>
                                <TableCell>
                                    {skill.queueIdreq === 1 
                                        ? (skill.isVoice ? SKILL_STRINGS.VOIP : SKILL_STRINGS.CHAT)
                                        : ""}
                                </TableCell>
                                <TableCell>
                                    {new Date(skill.CreateDate).toLocaleDateString('en-US')}
                                </TableCell>
                                <TableCell
                                    align={ALIGN.RIGHT}
                                >
                                    <Switch
                                        checked={skill.inactive === null || skill.inactive === 0}
                                        disabled
                                    />
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {SKILL_MESSAGES.NO_SKILLS}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredSkillsLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default SkillsTable;